import { type FC } from 'react';
import { isNil, upperFirst } from 'lodash';

import { cn, isUrl } from '@/lib/utils';
import type { EvaluationCheckpoint } from '@/types';

import type {
  CheckpointMetadataEntry,
  CheckpointStandardLink,
} from './CheckpointResult.types';

interface CheckpointResultBodyProps {
  checkpoint: EvaluationCheckpoint;
  activeCheckpointMetadata: CheckpointMetadataEntry[];
  hasStandards: boolean;
  standardsForCheckpoint: CheckpointStandardLink[];
  shouldShowHelp: boolean;
  countAfterMetadata: number;
  onOpenRemarks: () => void;
  onOpenAssessmentData: () => void;
  onOpenExceptionDetails: () => void;
  exceptionDetailsRows: Record<string, unknown>[];
  canOpenAssessmentData?: boolean;
}

const padNumber = (value: number) => {
  if (value < 10) return `0${value}`;
  return value.toString();
};

const CheckpointResultBody: FC<CheckpointResultBodyProps> = ({
  checkpoint,
  activeCheckpointMetadata,
  hasStandards,
  standardsForCheckpoint,
  shouldShowHelp,
  countAfterMetadata,
  onOpenRemarks,
  onOpenAssessmentData,
  onOpenExceptionDetails,
  exceptionDetailsRows,
  canOpenAssessmentData = false,
}) => {
  const itemsTable = checkpoint.items.length > 0 ? (
    <div className="checkpointResult__tableScroll w-full overflow-x-auto">
      <table className="itemsTable">
        <thead>
          <tr>
            <th>Item Id</th>
            <th>Item Type</th>
            <th>Reference</th>
            <th>System</th>
            <th>Remarks</th>
          </tr>
        </thead>
        <tbody>
          {checkpoint.items.map((item, index) => (
            <tr key={index}>
              <td>{item.itemId}</td>
              <td>{item.itemType}</td>
              <td>
                {isUrl(item.reference) ? (
                  <a
                    className="text-blue-500 underline"
                    href={item.reference}
                    target="__blank"
                  >
                    Reference link
                  </a>
                ) : (
                  item.reference
                )}
              </td>
              <td>{item.system}</td>
              <td
                className={cn({
                  pass: item.status === 'PASS',
                  na: item.status === 'NA',
                  fail: item.status === 'FAIL',
                })}
              >
                {item.status} - {item.remarks}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : null;

  const mobileItemsList = checkpoint.items.length > 0 ? (
    <div className="flex flex-col gap-2">
      {checkpoint.items.map((item, index) => (
        <div
          key={`mobile-item-${index}`}
          className="rounded-md border border-white/10 bg-[#232323] p-3"
        >
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-semibold text-white break-words">
              {item.itemId}
            </p>
            <span
              className={cn('shrink-0 text-xs font-semibold', {
                'text-green-400': item.status === 'PASS',
                'text-gray-400': item.status === 'NA',
                'text-red-400': item.status === 'FAIL',
              })}
            >
              {item.status}
            </span>
          </div>

          <dl className="mt-2 grid grid-cols-[5.5rem_1fr] gap-y-1 text-xs">
            <dt className="text-gray-400">Item Type</dt>
            <dd className="text-white break-words">{item.itemType}</dd>

            <dt className="text-gray-400">Reference</dt>
            <dd className="text-white break-words">
              {isUrl(item.reference) ? (
                <a
                  className="text-blue-400 underline break-words"
                  href={item.reference}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Reference link
                </a>
              ) : (
                item.reference
              )}
            </dd>

            <dt className="text-gray-400">System</dt>
            <dd className="text-white break-words">{item.system || '-'}</dd>

            <dt className="text-gray-400">Remarks</dt>
            <dd className="text-white break-words">{item.remarks}</dd>
          </dl>
        </div>
      ))}
    </div>
  ) : null;

  const checkpointDataLinks =
    canOpenAssessmentData ||
    checkpoint.assessmentData?.length > 0 ||
    exceptionDetailsRows.length > 0 ? (
      <div className="flex flex-col items-start gap-1">
        {canOpenAssessmentData || checkpoint.assessmentData?.length > 0 ? (
          <button
            className="w-max cursor-pointer text-blue-500 underline"
            onClick={onOpenAssessmentData}
          >
            View assessment data
          </button>
        ) : null}
        {exceptionDetailsRows.length > 0 ? (
          <button
            className="w-max cursor-pointer text-blue-500 underline"
            onClick={onOpenExceptionDetails}
          >
            View exception details
          </button>
        ) : null}
      </div>
    ) : null;

  const rawDetailsContent = hasStandards ? (
    <>
      <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2">
        <div className="flex gap-3">
          <span className="text-gray-500">01</span>
          <span className="text-green-500">
            Starting evaluation for {checkpoint.metadata.id}
          </span>
        </div>
        <div className="flex gap-3">
          <span className="text-green-500">
            This checkpoint is underpinned by the following standards and/or policies
          </span>
        </div>
      </div>
      <div className="flex gap-3 text-white">
        <span className="text-gray-500">02</span>
        ==================================================================================
        {hasStandards ? '==================================================================================' : ''}
      </div>

      <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2">
        <div>
          {activeCheckpointMetadata.map((item, itemIndex) => (
            <div className="flex gap-3" key={item.key}>
              <span className="text-gray-500">{padNumber(itemIndex + 3)}</span>
              <div className="flex gap-1 text-white">
                <span className="min-w-40">{upperFirst(item.key)}</span>
                <div className="flex flex-1 gap-2">
                  <span>:</span>
                  <span className="flex-1 break-words whitespace-pre-wrap">
                    {!isNil(item.value) ? item.value.toString() : ''}
                  </span>
                </div>
              </div>
            </div>
          ))}

          {shouldShowHelp ? (
            <div className="flex gap-3">
              <span className="text-gray-500">
                {padNumber(activeCheckpointMetadata.length + 3)}
              </span>
              <div className="flex gap-1 text-white">
                <span className="min-w-40">Help</span>
                <div className="flex flex-1 gap-2">
                  <span>:</span>
                  <span className="flex-1 break-words whitespace-pre-wrap">
                    {checkpoint.metadata.url ? (
                      <a
                        className="text-blue-500 underline"
                        href={`${checkpoint.metadata.url}#remediation`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        How to fix this checkpoint evaluation
                      </a>
                    ) : (
                      <span className="text-gray-500">
                        How to fix this checkpoint evaluation
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="md:border-l md:pl-6">
          <br />
          {standardsForCheckpoint.map((standard, index) => (
            <div className="flex gap-3" key={`${standard.url}-${index}`}>
              <div className="flex gap-1 text-white">
                <span className="min-w-40">Standard/Policy {index + 1}</span>
                <span className="flex-1 text-nowrap">
                  : &nbsp;
                  <a
                    className="text-blue-500 underline"
                    href={standard.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {standard.name}
                  </a>
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-3 text-white">
        <span className="text-gray-500">{padNumber(countAfterMetadata)}</span>
        ==================================================================================
        {hasStandards ? '==================================================================================' : ''}
      </div>
      <div className="flex gap-3 text-white">
        <span className="text-gray-500">{padNumber(countAfterMetadata + 1)}</span>
        <div className="flex-1">
          <span
            className={cn('text-nowrap', {
              'text-red-500': checkpoint.status === 'FAIL',
              'text-green-500': checkpoint.status === 'PASS',
              'text-gray-500': checkpoint.status === 'NA',
            })}
          >
            {checkpoint.remarks}
          </span>
          <button
            className="ml-2.5 cursor-pointer text-blue-500 underline"
            onClick={onOpenRemarks}
          >
            View full remarks
          </button>
        </div>
      </div>
    </>
  ) : (
    <>
      <div className="flex gap-3">
        <span className="text-gray-500">01</span>
        <span className="text-green-500">
          Starting evaluation for {checkpoint.metadata.id}
        </span>
      </div>
      <div className="flex gap-3 text-white">
        <span className="text-gray-500">02</span>
        ==================================================================================
      </div>
      {activeCheckpointMetadata.map((item, itemIndex) => (
        <div className="flex gap-3" key={item.key}>
          <span className="text-gray-500">{padNumber(itemIndex + 3)}</span>
          <div className="flex gap-1 text-white">
            <span className="min-w-40">{upperFirst(item.key)}</span>
            <span className="flex-1 text-nowrap">
              : &nbsp; {!isNil(item.value) ? item.value.toString() : ''}
            </span>
          </div>
        </div>
      ))}
      {shouldShowHelp ? (
        <div className="flex gap-3">
          <span className="text-gray-500">
            {padNumber(activeCheckpointMetadata.length + 3)}
          </span>
          <div className="flex gap-1 text-white">
            <span className="min-w-40">Help</span>
            <span className="flex-1 text-nowrap">
              : &nbsp;
              <a
                className="text-blue-500 underline"
                href={`${checkpoint.metadata.url}#remediation`}
                target="__blank"
              >
                How to fix this checkpoint evaluation
              </a>
            </span>
          </div>
        </div>
      ) : null}
      <div className="flex gap-3 text-white">
        <span className="text-gray-500">{padNumber(countAfterMetadata)}</span>
        ==================================================================================
      </div>
      <div className="flex gap-3 text-white">
        <span className="text-gray-500">{padNumber(countAfterMetadata + 1)}</span>
        <div className="flex-1">
          <span
            className={cn('text-nowrap', {
              'text-red-500': checkpoint.status === 'FAIL',
              'text-green-500': checkpoint.status === 'PASS',
              'text-gray-500': checkpoint.status === 'NA',
            })}
          >
            {checkpoint.remarks}
          </span>
          <button
            className="ml-2.5 cursor-pointer text-blue-500 underline"
            onClick={onOpenRemarks}
          >
            View full remarks
          </button>
        </div>
      </div>
    </>
  );

  const mobileSummaryContent = (
    <div className="flex flex-col gap-3 text-white">
      <div className="grid grid-cols-1 gap-2">
        {activeCheckpointMetadata.map((item, itemIndex) => (
          <div
            className="rounded-md border border-white/10 bg-[#232323] p-2.5"
            key={`summary-${item.key}-${itemIndex}`}
          >
            <p className="text-[0.7rem] uppercase tracking-wide text-gray-400">
              {upperFirst(item.key)}
            </p>
            <p className="mt-1 text-xs text-white break-words">
              {!isNil(item.value) ? item.value.toString() : ''}
            </p>
          </div>
        ))}
      </div>
    </div>
  );

  const mobileEvidenceContent = (
    <div className="flex flex-col gap-3 text-white">
      {hasStandards ? (
        <div className="rounded-md border border-white/10 bg-[#232323] p-3">
          <p className="text-[0.7rem] uppercase tracking-wide text-gray-400">
            Standards and Policies
          </p>
          <div className="mt-2 flex flex-col gap-1.5">
            {standardsForCheckpoint.map((standard, index) => (
              <div
                className="flex gap-2 text-xs"
                key={`evidence-standard-${index}-${standard.url}`}
              >
                <span className="text-gray-400">{index + 1}.</span>
                <a
                  className="text-blue-400 underline break-words"
                  href={standard.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {standard.name}
                </a>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-md border border-white/10 bg-[#232323] p-3 text-xs text-gray-300">
          No standards/policies found for this checkpoint.
        </div>
      )}

      {shouldShowHelp ? (
        <div className="rounded-md border border-white/10 bg-[#232323] p-3">
          <p className="text-[0.7rem] uppercase tracking-wide text-gray-400">
            Help
          </p>
          {checkpoint.metadata.url ? (
            <a
              className="mt-1 inline-block text-xs text-blue-400 underline break-words"
              href={`${checkpoint.metadata.url}#remediation`}
              target="_blank"
              rel="noopener noreferrer"
            >
              How to fix this checkpoint evaluation
            </a>
          ) : (
            <p className="mt-1 text-xs text-gray-300">
              How to fix this checkpoint evaluation
            </p>
          )}
        </div>
      ) : null}

      {mobileItemsList}
      {checkpointDataLinks}
    </div>
  );

  return (
    <div className="flex-1 min-h-0 w-full flex flex-col gap-4 overflow-auto px-4 py-4 max-sm:overflow-x-hidden max-sm:overflow-y-visible">
      <div className="sm:hidden">{mobileSummaryContent}</div>
      <div className="sm:hidden">{mobileEvidenceContent}</div>

      <div className="hidden sm:flex sm:min-w-0 sm:w-full sm:flex-col sm:font-inconsolata sm:text-sm">
        {rawDetailsContent}
        {itemsTable}
        {checkpointDataLinks}
      </div>
    </div>
  );
};

export default CheckpointResultBody;
